/* eslint-disable perfectionist/sort-objects */
import { useCallback, useState } from 'react';

import { $DatasetLicenses, licenseOptions } from '@databank/core';
import { Button, Form, Spinner } from '@douglasneuroinformatics/libui/components';
import { useNotificationsStore, useTranslation } from '@douglasneuroinformatics/libui/hooks';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon, FileUpIcon, UploadIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';

import { PageContainer } from '@/components/PageContainer';
import { PageHeading } from '@/components/PageHeading';
import { PrimaryKeySelector } from '@/components/PrimaryKeySelector';
import { useCreateDatasetMutation } from '@/hooks/mutations/useCreateDatasetMutation';
import { parseColumnNames } from '@/utils/csv';

const $CreateDatasetFormValidation = z.object({
  description: z.string().optional(),
  datasetType: z.enum(['BASE', 'TABULAR']),
  license: $DatasetLicenses,
  name: z.string().min(1)
});

type CreateDatasetFormData = z.infer<typeof $CreateDatasetFormValidation>;

const RouteComponent = () => {
  const MAX_UPLOAD_FILE_SIZE = 1024 * 1024 * 1024;
  const addNotification = useNotificationsStore((state) => state.addNotification);
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const createDatasetMutation = useCreateDatasetMutation();

  const [formData, setFormData] = useState<CreateDatasetFormData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [primaryKeys, setPrimaryKeys] = useState<string[]>([]);

  const resetFile = () => {
    setFile(null);
    setColumns([]);
    setPrimaryKeys([]);
  };

  const createDataset = () => {
    if (!formData) return;

    const requestFormData = new FormData();
    requestFormData.append('datasetType', String(formData.datasetType));
    requestFormData.append('license', String(formData.license));
    requestFormData.append('name', formData.name);
    requestFormData.append('description', formData.description ?? '');
    primaryKeys.forEach((key) => requestFormData.append('primaryKeys', key));
    requestFormData.append('isJSON', 'false');
    requestFormData.append('isReadyToShare', 'false');
    requestFormData.append('permission', 'MANAGER');

    if (file) {
      requestFormData.append('file', file);
    }

    createDatasetMutation.mutate(requestFormData, {
      onError() {
        addNotification({ type: 'error', message: t('createDatasetFailure') });
      },
      onSettled() {
        void navigate({ to: '/portal/datasets' });
      },
      onSuccess() {
        addNotification({ message: t('createDatasetSuccess'), type: 'success' });
      }
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const acceptedFile = acceptedFiles[0];
    if (!acceptedFile) {
      addNotification({
        type: 'error',
        message: t({ en: 'Unexpected file error', fr: 'Erreur de fichier inattendue' })
      });
    } else if (!acceptedFile.name.includes('.csv') && !acceptedFile.name.includes('.tsv')) {
      addNotification({
        type: 'error',
        message: t({ en: 'Only CSV or TSV files are allowed!', fr: 'Seuls les fichiers CSV ou TSV sont autorisés!' })
      });
    } else if (acceptedFile.size > MAX_UPLOAD_FILE_SIZE) {
      addNotification({
        type: 'error',
        message: t({ en: 'File size larger than 1 GB', fr: 'La taille du fichier est supérieure à 1 Go' })
      });
    } else {
      let parsedColumns: string[];
      try {
        parsedColumns = await parseColumnNames(acceptedFile);
      } catch {
        addNotification({
          type: 'error',
          message: t({
            en: 'Failed to read the column names from this file',
            fr: 'Échec de la lecture des noms de colonnes de ce fichier'
          })
        });
        return;
      }
      setFile(acceptedFile);
      setColumns(parsedColumns);
      setPrimaryKeys([]);
    }
  }, []);

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'], 'text/plain': ['.csv', '.tsv'] },
    maxFiles: 1,
    maxSize: MAX_UPLOAD_FILE_SIZE,
    onDrop: (acceptedFiles) => void onDrop(acceptedFiles)
  });

  const element = match(formData)
    .with(null, () => (
      <Form
        content={[
          {
            title: t({ en: 'Basic Dataset Information', fr: 'Informations de base du jeu de données' }),
            fields: {
              name: { kind: 'string', label: t('datasetName'), variant: 'input' },
              description: { kind: 'string', label: t('datasetDescription'), variant: 'textarea' },
              datasetType: {
                kind: 'string',
                label: t('datasetType'),
                options: { BASE: t('datasetTypeBase'), TABULAR: t('datasetTypeTabular') },
                variant: 'select'
              }
            }
          },
          {
            title: t({ en: 'Dataset License', fr: 'Licence du jeu de données' }),
            description: t({
              en: 'Search by license name or SPDX identifier',
              fr: 'Recherchez par nom de licence ou identifiant SPDX'
            }),
            fields: {
              license: {
                kind: 'string',
                label: t({ en: 'License', fr: 'Licence' }),
                options: licenseOptions,
                variant: 'combobox'
              }
            }
          }
        ]}
        submitBtnLabel={t({ en: 'Confirm', fr: 'Confirmer' })}
        validationSchema={$CreateDatasetFormValidation}
        onSubmit={(data) => setFormData(data)}
      />
    ))
    .with({ datasetType: 'BASE' }, () => (
      <div className="space-y-4 text-center">
        <p className="text-muted-foreground">{t('confirmCreateBaseDataset')}</p>
        <div className="flex gap-3">
          <Button className="flex-1" variant="secondary" onClick={() => setFormData(null)}>
            <ArrowLeftIcon className="mr-1.5 size-4" />
            {t('back')}
          </Button>
          <Button className="flex-1" onClick={() => createDataset()}>
            {t('confirm')}
          </Button>
        </div>
      </div>
    ))
    .otherwise(() =>
      createDatasetMutation.isPending ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner />
          <p className="text-muted-foreground mt-4 text-sm">
            {t({ en: 'Uploading dataset...', fr: 'Chargement du jeu de données...' })}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div
            {...getRootProps()}
            className="border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 transition-colors"
          >
            <input {...getInputProps()} />
            {file ? (
              <>
                <FileUpIcon className="text-primary size-10" />
                <p className="mt-3 text-sm font-medium">{file.name}</p>
                <p className="text-muted-foreground mt-1 text-xs">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
              </>
            ) : isDragActive ? (
              <>
                <UploadIcon className="text-primary size-10 animate-bounce" />
                <p className="mt-3 text-sm font-medium">{t('dropFile')}</p>
              </>
            ) : (
              <>
                <UploadIcon className="text-muted-foreground size-10" />
                <p className="mt-3 text-sm font-medium">{t('dropOrSelectFile')}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {t('onlyCsvTsv')} &middot; {t('maxFileSize')}
                </p>
              </>
            )}
          </div>
          {file && columns.length > 0 && (
            <PrimaryKeySelector columns={columns} value={primaryKeys} onChange={setPrimaryKeys} />
          )}
          <div className="flex gap-3">
            <Button className="flex-1" disabled={!file} variant="outline" onClick={resetFile}>
              {t('reset')}
            </Button>
            <Button
              className="flex-1"
              disabled={!file && formData?.datasetType !== 'BASE'}
              onClick={() => createDataset()}
            >
              <UploadIcon className="mr-1.5 size-4" />
              {t('submit')}
            </Button>
          </div>
        </div>
      )
    );

  return (
    <PageContainer width="form">
      <PageHeading centered>{t('createDataset')}</PageHeading>
      {element}
    </PageContainer>
  );
};

export const Route = createFileRoute('/portal/datasets/create')({
  component: RouteComponent
});
